
import React from 'react';
import { ModuleData } from './types';

export const SITE_NAME = "AviaGov";
export const SITE_URL = "https://www.aviagov.aero";

const IconWrap = ({ children }: { children?: React.ReactNode }) => (
  <div className="w-6 h-6 text-accent">{children}</div>
);

export const MODULES: ModuleData[] = [
  {
    id: 'eaip',
    name: 'eAIP Platform',
    shortDescription: 'Governed aeronautical information management and publication.',
    longDescription: 'Operate authoritative AIM data, AIRAC releases, eAIP editions, AIP Supplements, AICs, NOTAMs, Digital NOTAM events, PIBs, flight plans and public distribution through validation, maker-checker approval and durable evidence.',
    path: '/modules/eaip',
    metrics: [
      { label: 'NOTAM Creation', value: '3 min' },
      { label: 'Translation Time', value: '5 min' },
      { label: 'Manual Error Reduc.', value: '90%' }
    ],
    features: [
      { title: 'Governed NOTAM operations', description: 'Draft, validate, monitor and exchange NOTAM and Digital NOTAM events with explicit approval and reconciliation evidence.' },
      { title: 'AIRAC release control', description: 'Plan change sets, enforce freeze gates, rehearse releases and promote controlled publication packages atomically.' },
      { title: 'Authoritative datasets', description: 'Maintain traceable AIP, terrain, obstacle, procedure and chart datasets with provenance and quality checks.' },
      { title: 'Public distribution', description: 'Serve current, next and archive publications through controlled channels without implying autonomous approval.' }
    ],
    connectedFeatures: [
      { title: 'SMS Integration', description: 'Receives incident data to trigger mandatory NOTAM updates.', targetModule: 'SMS' },
      { title: 'PANS-OPS Sync', description: 'Auto-imports procedure designs with ICAO formatting.', targetModule: 'PANS-OPS' }
    ],
    compliance: ['ICAO Annex 15', 'EUROCONTROL Spec 3.0', 'ADQ Reg 73/2010']
  },
  {
    id: 'sms',
    name: 'Safety SMS',
    shortDescription: 'Occurrence, risk and operational safety assurance.',
    longDescription: 'Run governed occurrence reporting, investigation, risk assessment, SPI and FRMS workflows with Part 139 assurance, corrective actions, regulator exports and immutable evidence.',
    path: '/modules/sms',
    metrics: [
      { label: 'Incident-to-Pub', value: '3 Days' },
      { label: 'Reporting Speed', value: '2x Fast' },
      { label: 'Traceability', value: '100%' }
    ],
    features: [
      { title: 'Occurrence and investigation control', description: 'Capture reportable events, preserve source evidence, manage investigations and enforce closure gates and SLAs.' },
      { title: 'Risk and operational graphs', description: 'Author governed risk models, threats, controls, recovery paths and safety performance indicators.' },
      { title: 'FRMS and fatigue assurance', description: 'Apply fatigue-risk calculations, cumulative exposure logic and explainable operational evidence.' },
      { title: 'Part 139 assurance', description: 'Schedule inspections, track corrective actions and assemble regulator-ready assurance packages.' }
    ],
    connectedFeatures: [
      { title: 'Training Trigger', description: 'Auto-creates LMS training when investigation reveals competency gaps.', targetModule: 'LMS' },
      { title: 'NOTAM Link', description: 'Flags safety findings for immediate eAIP notification.', targetModule: 'eAIP' }
    ],
    compliance: ['ICAO Annex 19', 'EASA Part-145/CAMO']
  },
  {
    id: 'lms',
    name: 'Aviation LMS',
    shortDescription: 'Governed learning, qualification and competency records.',
    longDescription: 'Deliver regulated aviation training while governing curricula, objective evidence, assessments, live learning, qualification ledgers, recurrence and verifiable certification.',
    path: '/modules/lms',
    metrics: [
      { label: 'Cert Verification', value: 'Instant' },
      { label: 'Compliance Gap', value: 'Visible' },
      { label: 'Mobile Usage', value: 'High' }
    ],
    features: [
      { title: 'Course authoring and delivery', description: 'Build structured courses with rich content, SCORM, quizzes, code labs, dashboards and learner paths.' },
      { title: 'Competency and qualification', description: 'Map ICAO objectives, assessor decisions, qualification ledgers, recurrence and certificate evidence.' },
      { title: 'Live learning operations', description: 'Schedule cohorts and live events, manage attendance and issue governed engagement reminders.' },
      { title: 'Compliance packs', description: 'Activate role- and organization-specific regulatory structures while retaining one governed training record.' }
    ],
    connectedFeatures: [
      { title: 'SMS Feedback', description: 'Training completion data flows back to verify safety readiness.', targetModule: 'SMS' },
      { title: 'Procedure Updates', description: 'Deploys new training automatically when PANS-OPS updates procedures.', targetModule: 'PANS-OPS' }
    ]
  },
  {
    id: 'pans-ops',
    name: 'PANSOPS Designer',
    shortDescription: 'Governed instrument procedure design and chart production.',
    longDescription: 'Design, validate and release SID, STAR and approach procedures with PANS-OPS calculations, terrain and obstacle evidence, precision map tooling and controlled eAIP handoff.',
    path: '/modules/pans-ops',
    metrics: [
      { label: 'Design Time', value: '-60%' },
      { label: 'Calc Accuracy', value: 'Absolute' },
      { label: '3D Visibility', value: 'Total' }
    ],
    features: [
      { title: 'Procedure workspace', description: 'Edit governed procedure geometry with keyboard-accessible map tools and explicit workflow phases.' },
      { title: 'PANS-OPS validation', description: 'Apply deterministic minima, turn, holding, approach and missed-approach checks with explainable blockers.' },
      { title: 'Terrain and obstacle evidence', description: 'Reconcile authoritative terrain and obstacle inputs while preserving lineage and safety boundaries.' },
      { title: 'Controlled chart release', description: 'Generate chart artifacts only after assessment, approval and publication handoff controls pass.' }
    ],
    connectedFeatures: [
      { title: 'eAIP Flow', description: 'Exports approved designs directly to the publication workflow.', targetModule: 'eAIP' },
      { title: 'Safety Review', description: 'Receives safety findings to flag required procedure revisions.', targetModule: 'SMS' }
    ]
  }
];

// Capability inventory derived from the read-only eAIP TypeScript source tree.
// This is intentionally product-facing: it gives authorities a truthful map of
// the operational surfaces that the platform is designed to support.
export type CapabilityArea = 'AIM' | 'Procedure studio' | 'Safety & training' | 'Connectivity & governance';
export interface PlatformCapability {
  key: string;
  name: string;
  area: CapabilityArea;
  description: string;
  sourceSignals: string[];
  dependencies?: string[];
}

export const PLATFORM_CAPABILITIES: PlatformCapability[] = [
  { key: 'aim-core', name: 'AIM core and authoritative data', area: 'AIM', description: 'Authoritative aeronautical data, temporal validity, validation, AIXM conformance and provenance.', sourceSignals: ['aimHash', 'aixmConformance', 'aeronauticalFeatureIdentity'] },
  { key: 'airac', name: 'AIRAC release control', area: 'AIM', description: '28/56-day calendars, change sets, freeze gates, release rehearsals and atomic promotion.', sourceSignals: ['airacRelease', 'airacPlanService', 'changeSetService'], dependencies: ['AIM core'] },
  { key: 'publication', name: 'eAIP, AIP Supplements and AIC', area: 'AIM', description: 'Controlled authoring, approval, versioned publication packages, archives and public distribution.', sourceSignals: ['publicationService', 'publicationPackage', 'referenceReleaseService'], dependencies: ['AIRAC'] },
  { key: 'notam', name: 'NOTAM and Digital NOTAM operations', area: 'AIM', description: 'Drafting, validation, monitoring, exchange, structured events, geometry and durable reconciliation.', sourceSignals: ['bulletinService', 'bulletinValidation', 'bulletinMonitoring', 'flymatesNotamDraft'], dependencies: ['AIM core'] },
  { key: 'met', name: 'Meteorological information', area: 'AIM', description: 'Operational MET source normalization, freshness monitoring and retained evidence reprocessing.', sourceSignals: ['aim-met-operations', 'pibRelevance'] },
  { key: 'pib', name: 'Preflight Information Briefings', area: 'AIM', description: 'Route, aerodrome and area briefings composed from AIM, NOTAM and MET sources.', sourceSignals: ['pibQuery', 'pibBriefingService', 'pibRendering'], dependencies: ['NOTAM'] },
  { key: 'fpl', name: 'Flight plan operations', area: 'AIM', description: 'ICAO flight-plan composition, validation, workflow, submission, amendment, delay and cancellation.', sourceSignals: ['flightPlanService', 'fplValidation', 'fplSubmissionService'] },
  { key: 'datasets', name: 'Aeronautical datasets and chart products', area: 'AIM', description: 'Controlled AIP, terrain, obstacle, mapping, procedure datasets and chart artifact production.', sourceSignals: ['datasetPackageService', 'chartProductService', 'chartArtifactStore'] },
  { key: 'procedure-studio', name: 'Instrument procedure studio', area: 'Procedure studio', description: 'SID, STAR and approach design with PANS-OPS calculations, terrain, precision map tooling and evidence.', sourceSignals: ['panops* tests', 'procedureGovernanceArtifacts', 'procedure-studio-precision-map'] },
  { key: 'assessment', name: 'Procedure assurance and assessment trace', area: 'Procedure studio', description: 'Safety states, diagnostic boundaries, immutable source evidence and governed release controls.', sourceSignals: ['procedure-studio-assessment-trace', 'procedure-studio-operational-assurance'] },
  { key: 'sms', name: 'Safety management and risk governance', area: 'Safety & training', description: 'Hazard reporting, risk models, bow-tie controls, Part 139 assurance, inspections and corrective actions.', sourceSignals: ['sms-risk-model-editor', 'sms-part139-assurance', 'sms-part139-scheduling'] },
  { key: 'lms', name: 'Competency and training management', area: 'Safety & training', description: 'Role-based learning, competency gaps, certificates and safety-triggered training workflows.', sourceSignals: ['lmsClient', 'training governance settings'] },
  { key: 'workflow', name: 'Workflow inbox and maker-checker governance', area: 'Safety & training', description: 'Phase guidance, approvals, bulk-safe reminders, SLA escalation, rollback and auditable transitions.', sourceSignals: ['workflowInbox', 'workflowStatusPolicy', 'workflowTemplatePolicy'] },
  { key: 'connectivity', name: 'Protected aviation connectivity', area: 'Connectivity & governance', description: 'Organization network boundaries, VPN lifecycle, topology, HA, recovery and health evidence.', sourceSignals: ['vpnLifecycle', 'connectivityTopology', 'gatewayHa', 'aim-vpn-lifecycle'] },
  { key: 'messaging', name: 'AFTN and AMHS messaging', area: 'Connectivity & governance', description: 'Durable ATS message exchange, addressing, acknowledgements, replay and redacted transport evidence.', sourceSignals: ['aftnGatewayService', 'amhsGatewayService', 'atsStoreForward'] },
  { key: 'integrations', name: 'AIM integrations and adapters', area: 'Connectivity & governance', description: 'Scoped inbound/outbound adapters for AFTN, AMHS, MET and external aeronautical systems.', sourceSignals: ['aim-aftn-adapter', 'aim-amhs-adapter', 'aim-integrations'] },
  { key: 'access', name: 'Organization access and capability management', area: 'Connectivity & governance', description: 'Tenant context, RBAC, explicit capability flags, fail-closed access and audited changes.', sourceSignals: ['capabilities.ts', 'rbac.ts', 'aim-capability-management'] },
  { key: 'compliance', name: 'Compliance, quality and operational assurance', area: 'Connectivity & governance', description: 'Rule packs, quality workspaces, evidence, SLOs, go-live readiness and controlled waivers.', sourceSignals: ['qualityService', 'operationalSloAssessment', 'goLiveAssessment', 'aim-quality'] },
  { key: 'public-portal', name: 'Public AIP portal and distribution', area: 'Connectivity & governance', description: 'Current, next and archive views with controlled publication channels and recipient acknowledgement.', sourceSignals: ['public AIP portal', 'publicationChannelService', 'aim-publications'] },
  { key: 'sms-occurrences', name: 'SMS occurrence and investigation management', area: 'Safety & training', description: 'Occurrence reporting, reportability, investigation, closure gates, SLAs, reminders and regulator-ready outputs.', sourceSignals: ['occurrenceRules', 'investigationControlledOutput', 'occurrenceSlaEscalation', 'regulatorExports'] },
  { key: 'sms-risk', name: 'SMS risk, SPI and fatigue management', area: 'Safety & training', description: 'Risk model authoring, operational graphs, safety performance indicators, FRMS calculations and evidence governance.', sourceSignals: ['riskModelAuthoring', 'operationalGraph', 'spi.ts', 'frmsAutomation', 'evidenceGovernance'] },
  { key: 'sms-part139', name: 'Part 139 inspections and assurance', area: 'Safety & training', description: 'Inspection scheduling, mobile sync, assurance packages, compliance templates and corrective action follow-up.', sourceSignals: ['part139InspectionService', 'part139SchedulingService', 'part139AssuranceService'] },
  { key: 'lms-authoring', name: 'LMS course authoring and delivery', area: 'Safety & training', description: 'Course structures, rich content, SCORM, code labs, quizzes, grading, dashboards and learner paths.', sourceSignals: ['courseStructure', 'scorm', 'codeLabService', 'gradebook', 'learnerPaths'] },
  { key: 'lms-live', name: 'LMS live learning and engagement', area: 'Safety & training', description: 'Cohorts, live events, LiveKit sessions, reminders, notifications and attendance evidence.', sourceSignals: ['liveEventSchedule', 'cohortScheduling', 'livekit', 'engagementNotifications'] },
  { key: 'lms-compliance', name: 'LMS competency and certification', area: 'Safety & training', description: 'ICAO frameworks, objectives, qualification ledger, certificates, compliance packs and renewal reminders.', sourceSignals: ['icaoFrameworkFamilies', 'objectiveEvaluation', 'qualificationLedger', 'compliancePacks'] },
  { key: 'utm', name: 'UTM and drone operations', area: 'Connectivity & governance', description: 'Tenant-scoped UTM configuration, volumes, provider integrations, audit, rate limiting and certificates.', sourceSignals: ['utm/config', 'utm/tenant', 'utm/integrations', 'certificatePdf'] },
  { key: 'flymate', name: 'FlyMate AI assistant and knowledge assurance', area: 'Connectivity & governance', description: 'Document-grounded assistance, knowledge inventory, response feedback and assurance boundaries with human control.', sourceSignals: ['DocumentAIAssistant', 'FlyMatesKnowledgeInventory', 'AIResponseAssurance', 'chat.ts'] },
  { key: 'dms', name: 'Document management and collaboration', area: 'Connectivity & governance', description: 'Files, folders, Office-style editing, version history, sharing, approvals, checklists and document diffs.', sourceSignals: ['FileFolderBrowser', 'WordEditor', 'ExcelEditor', 'VersionHistoryModal', 'DocumentDiff'] },
  { key: 'identity', name: 'Identity, mobile access and notifications', area: 'Connectivity & governance', description: 'OTP/public auth, mobile sessions, PWA updates, push notifications, organization context and role normalization.', sourceSignals: ['publicLoginOtp', 'mobileAuth', 'sessionClient', 'mobilePush', 'organizationState'] },
];

export const DEPLOYMENT_MODELS = [
  {
    name: 'SaaS',
    desc: 'Fastest deployment (4-8 weeks). Automatic updates, managed infrastructure.',
    bestFor: 'Rapid modernization with lowest TCO.'
  },
  {
    name: 'Private Cloud',
    desc: 'Dedicated single-tenant infrastructure. Enhanced custom security configs.',
    bestFor: 'National authorities requiring isolated resources.'
  },
  {
    name: 'On-Premises',
    desc: 'Full data sovereignty on your hardware. Ubuntu/RHEL supported.',
    bestFor: 'Strict national security and air defense requirements.'
  }
];

export const PRICING_PLANS = [
  {
    name: 'Regional',
    description: 'For regional airports and smaller operators.',
    price: 'SaaS Subscription',
    features: ['Up to 2 Modules', '50 Active Users', 'Standard Support', 'Cloud Infrastructure']
  },
  {
    name: 'Authority',
    description: 'National Civil Aviation Authorities.',
    price: 'Enterprise Quote',
    features: ['All 4 Core Modules', 'Unlimited Users', '24/7 Priority Support', 'Dedicated Private Cloud']
  },
  {
    name: 'Government',
    description: 'State-level defense and airspace groups.',
    price: 'Custom Agreement',
    features: ['Full Suite + FlyMate', 'Global Integration API', 'On-Premise Deployment', 'Dedicated Success Team']
  }
];

export const PLATFORM_FEATURES = [
  {
    title: 'Integrated Core',
    description: 'Governed evidence flows between Safety (SMS), Training (LMS), Procedure Design and AIM publication when explicitly enabled.',
    icon: (
      <IconWrap>
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
      </IconWrap>
    )
  },
  {
    title: 'FlyMate grounded assistance',
    description: 'NOTAMs created in 3 minutes. ICAO-compliant translations in 5 minutes.',
    icon: (
      <IconWrap>
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
      </IconWrap>
    )
  },
  {
    title: 'Regulatory Native',
    description: 'Compliance with ICAO Annex 15, 19, and Doc 8168 built directly into the schema.',
    icon: (
      <IconWrap>
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
      </IconWrap>
    )
  },
  {
    title: 'Automated Workflows',
    description: 'Replace informal handoffs with controlled workflow transitions, approvals and durable audit evidence across departments.',
    icon: (
      <IconWrap>
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
      </IconWrap>
    )
  }
];

export const BLOG_POSTS = [
  {
    id: 1,
    category: "Technology",
    title: "AI in Aeronautical Information Management",
    excerpt: "How generative AI is transforming NOTAM creation and AIRAC cycle validation for modern ANSPs.",
    date: "Oct 24, 2024"
  },
  {
    id: 2,
    category: "Compliance",
    title: "Preparing for ICAO Annex 15 Amendments",
    excerpt: "A deep dive into the upcoming digital data quality requirements and how to ensure your authority is ready.",
    date: "Oct 15, 2024"
  },
  {
    id: 3,
    category: "Safety",
    title: "Closing the Safety Loop",
    excerpt: "Linking Safety Management Systems (SMS) with eAIP publication to ensure mission-critical safety data reaches pilots instantly.",
    date: "Oct 02, 2024"
  }
];
